import { supabase } from '@/integrations/supabase/client';
import type { Cliente, ClienteStatus } from '@/features/clientes/types/cliente';

export interface ListarClientesResponse {
  data: Cliente[];
  total: number;
  page: number;
  per_page: number;
  total_pages: number;
}

// Mapeamento de nomes de estados para siglas
const estadoNomeParaSigla: { [key: string]: string } = {
  'Acre': 'AC',
  'Alagoas': 'AL',
  'Amapá': 'AP',
  'Amazonas': 'AM',
  'Bahia': 'BA',
  'Ceará': 'CE',
  'Distrito Federal': 'DF',
  'Espírito Santo': 'ES',
  'Goiás': 'GO',
  'Maranhão': 'MA',
  'Mato Grosso': 'MT',
  'Mato Grosso do Sul': 'MS',
  'Minas Gerais': 'MG',
  'Pará': 'PA',
  'Paraíba': 'PB',
  'Paraná': 'PR',
  'Pernambuco': 'PE',
  'Piauí': 'PI',
  'Rio de Janeiro': 'RJ',
  'Rio Grande do Norte': 'RN',
  'Rio Grande do Sul': 'RS',
  'Rondônia': 'RO',
  'Roraima': 'RR',
  'Santa Catarina': 'SC',
  'São Paulo': 'SP',
  'Sergipe': 'SE',
  'Tocantins': 'TO'
};

const mapearCliente = (cliente: any): Cliente => {
  // Converter nome do estado para sigla se necessário
  let estadoMapeado = cliente.estado || "";
  if (cliente.estado && estadoNomeParaSigla[cliente.estado]) {
    estadoMapeado = estadoNomeParaSigla[cliente.estado];
  }

  return {
    id: cliente.id.toString(),
    nome: cliente.nome || "",
    email: cliente.email || "",
    cpf: cliente.cpf || "",
    tel1: cliente.tel1 || cliente.telefone || "",
    tel2: cliente.tel2 || "",
    data_nascimento: cliente.data_nascimento || null,
    rg: cliente.rg || "",
    data_emissao_rg: cliente.data_emissao_rg || null,
    naturalidade: cliente.naturalidade || "",
    nacionalidade: cliente.nacionalidade || "",
    atividade_desenvolvida: cliente.atividade_desenvolvida || "",
    rendimento_titular: cliente.rendimento_titular || "",
    nome_mae: cliente.nome_mae || "",
    nome_pai: cliente.nome_pai || "",
    estado_civil: cliente.estado_civil || 'solteiro',
    nome_conjuge: cliente.nome_conjuge || "",
    cpf_conjuge: cliente.cpf_conjuge || "",
    data_nascimento_conjuge: cliente.data_nascimento_conjuge || null,
    banco: cliente.banco || "",
    agencia: cliente.agencia || "",
    conta: cliente.conta || "",
    tipo_conta: cliente.tipo_conta || undefined,
    logradouro: cliente.logradouro || "",
    numero: cliente.numero || "",
    complemento: cliente.complemento || "",
    bairro: cliente.bairro || "",
    cep: cliente.cep || "",
    cidade: cliente.cidade || "",
    estado: estadoMapeado,
    documento_com_foto_doc: cliente.documento_com_foto_doc,
    residencia_doc: cliente.residencia_doc,
    created_at: cliente.created_at || "",
    created_by: cliente.created_by || "",
    tenant_id: cliente.tenant_id?.toString() || "",
    status: cliente.status as ClienteStatus || 'ativo',
    tenant: cliente.tenant,
    updated_at: cliente.updated_at || "",
    contratos: cliente.contratos || [],
    // Campos de qualificação
    tem_entrada: cliente.tem_entrada || false,
    tipo_bem: cliente.tipo_bem,
    valor_entrada: cliente.valor_entrada,
    valor_parcela_orcamento: cliente.valor_parcela_orcamento,
    tempo_procura: cliente.tempo_procura,
    prioridade: cliente.prioridade,
    tempo_aluguel: cliente.tempo_aluguel,
    cliente_qualificado: cliente.cliente_qualificado || false,
    status_atual: cliente.status_atual || "",
    // Campos de sistema de temperatura
    temperatura_lead: cliente.temperatura_lead,
    ligacoes_realizadas: cliente.ligacoes_realizadas || 0,
    data_primeiro_contato: cliente.data_primeiro_contato,
    // Campos adicionais
    observacoes: cliente.observacoes,
    status_cadastro: cliente.status_cadastro || 'COMPLETO',
    // Campos para Pessoa Jurídica
    razao_social: cliente.razao_social,
    inscricao_estadual: cliente.inscricao_estadual,
    nome_responsavel: cliente.nome_responsavel,
    cpf_responsavel: cliente.cpf_responsavel,
    email_responsavel: cliente.email_responsavel
  };
};

export class ClientesSupabaseService {
  async listarClientes(
    page = 1,
    per_page = 10,
    search?: string,
    tenantId?: string | number | number[],
    filterByUserId?: string
  ): Promise<ListarClientesResponse> {
    try {
      const { applyTenantIdFilter } = await import('@/utils/tenantScope');

      // Primeiro, buscar o total de registros para paginação
      let countQuery = supabase
        .from('clientes')
        .select('*', { count: 'exact', head: true });

      countQuery = applyTenantIdFilter(countQuery, tenantId);

      // Aplicar filtro por usuário (para vendedores)
      if (filterByUserId) {
        if (filterByUserId.includes(',')) {
          const userIds = filterByUserId.split(',').map(id => id.trim());
          countQuery = countQuery.in('created_by', userIds);
        } else {
          countQuery = countQuery.eq('created_by', filterByUserId);
        }
      }

      // Aplicar filtro de busca no count se fornecido
      if (search) {
        countQuery = countQuery.or(`nome.ilike.%${search}%,email.ilike.%${search}%,cpf.ilike.%${search}%`);
      }

      const { count, error: countError } = await countQuery;

      if (countError) {
        console.error('Erro ao contar clientes:', countError);
        throw countError;
      }

      // Agora buscar os dados paginados
      let dataQuery = supabase
        .from('clientes')
        .select(`
          *,
          tenant:tenants(id, nome),
          contratos:contratos(id, status, created_at, bens(preco_bem))
        `);

      dataQuery = applyTenantIdFilter(dataQuery, tenantId);

      // Aplicar filtro por usuário (para vendedores)
      if (filterByUserId) {
        if (filterByUserId.includes(',')) {
          const userIds = filterByUserId.split(',').map(id => id.trim());
          dataQuery = dataQuery.in('created_by', userIds);
        } else {
          dataQuery = dataQuery.eq('created_by', filterByUserId);
        }
      }

      // Aplicar filtro de busca nos dados se fornecido
      if (search) {
        dataQuery = dataQuery.or(`nome.ilike.%${search}%,email.ilike.%${search}%,cpf.ilike.%${search}%`);
      }

      // Aplicar ordenação por data de criação (mais recentes primeiro) e por ID
      dataQuery = dataQuery.order('created_at', { ascending: false }).order('id', { ascending: false });

      // Aplicar paginação
      const startIndex = (page - 1) * per_page;
      const endIndex = startIndex + per_page - 1;
      dataQuery = dataQuery.range(startIndex, endIndex);

      const { data, error } = await dataQuery;

      if (error) {
        console.error('Erro ao buscar clientes:', error);
        throw error;
      }

      const clientes = data || [];
      const total = count || 0;
      const totalPages = Math.ceil(total / per_page);

      return {
        data: clientes.map(mapearCliente),
        total,
        page,
        per_page,
        total_pages: totalPages
      };
    } catch (error) {
      console.error('Erro no ClientesSupabaseService.listarClientes:', error);
      throw error;
    }
  }

  async buscarCliente(id: string): Promise<Cliente> {
    try {
      const { data, error } = await supabase
        .from('clientes')
        .select(`
          *,
          tenant:tenants(id, nome),
          contratos:contratos(id, status, contrato_numero, bens(preco_bem))
        `)
        .eq('id', Number(id))
        .single();

      if (error) {
        console.error('Erro ao buscar cliente:', error);
        throw error;
      }

      return mapearCliente(data);
    } catch (error) {
      console.error('Erro no ClientesSupabaseService.buscarCliente:', error);
      throw error;
    }
  }

  async criarCliente(cliente: Partial<Cliente>): Promise<Cliente> {
    try {
      const clienteData = {
        nome: cliente.nome,
        email: cliente.email,
        cpf: cliente.cpf,
        tel1: cliente.tel1,
        tel2: cliente.tel2,
        data_nascimento: cliente.data_nascimento,
        rg: cliente.rg,
        data_emissao_rg: cliente.data_emissao_rg,
        naturalidade: cliente.naturalidade,
        nacionalidade: cliente.nacionalidade,
        atividade_desenvolvida: cliente.atividade_desenvolvida,
        rendimento_titular: cliente.rendimento_titular,
        nome_mae: cliente.nome_mae,
        nome_pai: cliente.nome_pai,
        estado_civil: cliente.estado_civil,
        nome_conjuge: cliente.nome_conjuge,
        cpf_conjuge: cliente.cpf_conjuge,
        data_nascimento_conjuge: cliente.data_nascimento_conjuge,
        banco: cliente.banco,
        agencia: cliente.agencia,
        conta: cliente.conta,
        tipo_conta: cliente.tipo_conta,
        logradouro: cliente.logradouro,
        numero: cliente.numero,
        complemento: cliente.complemento,
        bairro: cliente.bairro,
        cep: cliente.cep,
        cidade: cliente.cidade,
        estado: cliente.estado,
        documento_com_foto_doc: cliente.documento_com_foto_doc,
        residencia_doc: cliente.residencia_doc,
        tenant_id: Number(cliente.tenant_id),
        created_by: cliente.created_by,
        status: cliente.status || 'ativo',
        tem_entrada: cliente.tem_entrada,
        tipo_bem: cliente.tipo_bem,
        valor_entrada: cliente.valor_entrada,
        valor_parcela_orcamento: cliente.valor_parcela_orcamento,
        tempo_procura: cliente.tempo_procura,
        prioridade: cliente.prioridade,
        tempo_aluguel: cliente.tempo_aluguel,
        cliente_qualificado: cliente.cliente_qualificado,
        status_atual: cliente.status_atual,
        temperatura_lead: cliente.temperatura_lead || 'MEDIO',
        ligacoes_realizadas: cliente.ligacoes_realizadas || 0,
        data_primeiro_contato: cliente.data_primeiro_contato
      };

      const { data, error } = await supabase
        .from('clientes')
        .insert([clienteData as any])
        .select(`
          *,
          tenant:tenants(id, nome)
        `)
        .single();

      if (error) {
        console.error('Erro ao criar cliente:', error);
        throw error;
      }

      return mapearCliente(data);
    } catch (error) {
      console.error('Erro no ClientesSupabaseService.criarCliente:', error);
      throw error;
    }
  }

  async atualizarCliente(id: string, cliente: Partial<Cliente>): Promise<Cliente> {
    try {
      const clienteData = {
        nome: cliente.nome,
        email: cliente.email,
        cpf: cliente.cpf,
        tel1: cliente.tel1,
        tel2: cliente.tel2,
        data_nascimento: cliente.data_nascimento,
        rg: cliente.rg,
        data_emissao_rg: cliente.data_emissao_rg,
        naturalidade: cliente.naturalidade,
        nacionalidade: cliente.nacionalidade,
        atividade_desenvolvida: cliente.atividade_desenvolvida,
        rendimento_titular: cliente.rendimento_titular,
        nome_mae: cliente.nome_mae,
        nome_pai: cliente.nome_pai,
        estado_civil: cliente.estado_civil,
        nome_conjuge: cliente.nome_conjuge,
        cpf_conjuge: cliente.cpf_conjuge,
        data_nascimento_conjuge: cliente.data_nascimento_conjuge,
        banco: cliente.banco,
        agencia: cliente.agencia,
        conta: cliente.conta,
        tipo_conta: cliente.tipo_conta,
        logradouro: cliente.logradouro,
        numero: cliente.numero,
        complemento: cliente.complemento,
        bairro: cliente.bairro,
        cep: cliente.cep,
        cidade: cliente.cidade,
        estado: cliente.estado,
        documento_com_foto_doc: cliente.documento_com_foto_doc,
        residencia_doc: cliente.residencia_doc,
        tenant_id: Number(cliente.tenant_id),
        created_by: cliente.created_by,
        status: cliente.status,
        updated_at: new Date().toISOString(),
        tem_entrada: cliente.tem_entrada,
        tipo_bem: cliente.tipo_bem,
        valor_entrada: cliente.valor_entrada,
        valor_parcela_orcamento: cliente.valor_parcela_orcamento,
        tempo_procura: cliente.tempo_procura,
        prioridade: cliente.prioridade,
        tempo_aluguel: cliente.tempo_aluguel,
        cliente_qualificado: cliente.cliente_qualificado,
        status_atual: cliente.status_atual,
        temperatura_lead: cliente.temperatura_lead,
        ligacoes_realizadas: cliente.ligacoes_realizadas,
        data_primeiro_contato: cliente.data_primeiro_contato
      };

      const { data, error } = await supabase
        .from('clientes')
        .update(clienteData as any)
        .eq('id', Number(id))
        .select(`
          *,
          tenant:tenants(id, nome)
        `)
        .single();

      if (error) {
        console.error('Erro ao atualizar cliente:', error);
        throw error;
      }

      return mapearCliente(data);
    } catch (error) {
      console.error('Erro no ClientesSupabaseService.atualizarCliente:', error);
      throw error;
    }
  }

  async excluirCliente(id: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('clientes')
        .delete()
        .eq('id', Number(id));

      if (error) {
        console.error('Erro ao excluir cliente:', error);
        throw error;
      }
    } catch (error) {
      console.error('Erro no ClientesSupabaseService.excluirCliente:', error);
      throw error;
    }
  }

  async excluirClientesEmLote(ids: string[]): Promise<{ message: string; deletedCount: number; deletedIds: number[] }> {
    try {
      const { error, count } = await supabase
        .from('clientes')
        .delete()
        .in('id', ids.map(id => parseInt(id)));

      if (error) {
        console.error('Erro ao excluir clientes em lote:', error);
        throw error;
      }

      return {
        message: `${count || 0} clientes excluídos com sucesso`,
        deletedCount: count || 0,
        deletedIds: ids.map(id => parseInt(id))
      };
    } catch (error) {
      console.error('Erro no ClientesSupabaseService.excluirClientesEmLote:', error);
      throw error;
    }
  }

  async verificarCPFExistente(cpf: string, ignoreClienteId?: string): Promise<boolean> {
    try {
      let query = supabase
        .from('clientes')
        .select('id')
        .eq('cpf', cpf.replace(/\D/g, ''));

      if (ignoreClienteId) {
        query = query.neq('id', Number(ignoreClienteId));
      }

      const { data, error } = await query;

      if (error) {
        console.error('Erro ao verificar CPF:', error);
        throw error;
      }

      return (data && data.length > 0);
    } catch (error) {
      console.error('Erro no ClientesSupabaseService.verificarCPFExistente:', error);
      throw error;
    }
  }

  async uploadDocumento(clienteId: string, file: File, tipo: string, tenantId?: string, userId?: string): Promise<any> {
    try {
      const fileExt = file.name.split('.').pop();
      const timestamp = Date.now();
      const fileName = `${tipo}_${timestamp}.${fileExt}`;
      const filePath = `cliente_${clienteId}/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('documentos-clientes')
        .upload(filePath, file);

      if (uploadError) {
        console.error('Erro no upload para Storage:', uploadError);
        throw uploadError;
      }

      const { data: documentoData, error: dbError } = await supabase
        .from('documentos_cliente')
        .insert([{
          cliente_id: parseInt(clienteId),
          tenant_id: tenantId ? parseInt(tenantId) : 1,
          usuario_upload_id: userId ? (userId.length > 10 ? 1 : parseInt(userId)) : 1,
          nome_arquivo: file.name,
          caminho_arquivo: filePath,
          tipo_documento: tipo
        }])
        .select()
        .single();

      if (dbError) {
        console.error('Erro ao registrar documento no banco:', dbError);
        await supabase.storage
          .from('documentos-clientes')
          .remove([filePath]);
        throw dbError;
      }

      return { success: true, data: documentoData };
    } catch (error) {
      console.error('Erro no ClientesSupabaseService.uploadDocumento:', error);
      throw error;
    }
  }

  async listarDocumentos(clienteId: string): Promise<any[]> {
    try {
      const { data: docs, error } = await supabase
        .from('documentos_cliente')
        .select('*')
        .eq('cliente_id', parseInt(clienteId))
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Erro ao listar documentos:', error);
        throw error;
      }

      if (!docs || docs.length === 0) return [];

      const paths = docs.map(doc => doc.caminho_arquivo);
      const { data: urlData, error: urlError } = await supabase.storage
        .from('documentos-clientes')
        .createSignedUrls(paths, 3600);

      if (urlError) {
        console.warn('Erro ao gerar URLs assinadas:', urlError);
        return docs;
      }

      return docs.map(doc => {
        const urlObj = urlData.find(u => u.path === doc.caminho_arquivo);
        return {
          ...doc,
          url: urlObj?.signedUrl || null
        };
      });
    } catch (error) {
      console.error('Erro no ClientesSupabaseService.listarDocumentos:', error);
      throw error;
    }
  }

  async excluirDocumento(clienteId: string, documentoId: string): Promise<void> {
    try {
      const { data: documento, error: fetchError } = await supabase
        .from('documentos_cliente')
        .select('caminho_arquivo')
        .eq('id', parseInt(documentoId))
        .eq('cliente_id', parseInt(clienteId))
        .single();

      if (fetchError || !documento) {
        throw new Error('Documento não encontrado');
      }

      const { error: storageError } = await supabase.storage
        .from('documentos-clientes')
        .remove([documento.caminho_arquivo]);

      if (storageError) {
        console.warn('Erro ao excluir do storage:', storageError);
      }

      const { error: dbError } = await supabase
        .from('documentos_cliente')
        .delete()
        .eq('id', parseInt(documentoId))
        .eq('cliente_id', parseInt(clienteId));

      if (dbError) {
        throw dbError;
      }
    } catch (error) {
      console.error('Erro no ClientesSupabaseService.excluirDocumento:', error);
      throw error;
    }
  }

  async getDocumentUrl(path: string): Promise<string | null> {
    try {
      const { data, error } = await supabase.storage
        .from('documentos-clientes')
        .createSignedUrl(path, 3600);

      if (error) {
        console.error('Erro ao gerar URL assinada:', error);
        return null;
      }

      return data.signedUrl;
    } catch (error) {
      console.error('Erro no ClientesSupabaseService.getDocumentUrl:', error);
      return null;
    }
  }
}

export const clientesSupabaseService = new ClientesSupabaseService();
